import React, { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import PropTypes from 'prop-types';
import { useRecoilValue } from 'recoil';

import { hourlyHourForecastTileTestIDs, Theme } from '@constants';
import { dayDateTimeState } from '@store';

import { roundNearest5 } from '../../../helpers/DataFormatting';
import { displayString, isDisplayable, missingValue } from '../../../helpers/StringUtils';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { FocusableHighlight } from '../../focusable/FocusableHighlight/FocusableHighlight';
import { TWCIcon } from '../../primitives/TWCIcon/TWCIcon';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'hourlyHourForecastTile.';

const ForecastTileContent = memo(
  ({ hour, day, iconCodeExtend, temperature, precipChance, isLoading, error, focused = false }) => {
    const { t } = useTranslation();

    return (
      <View style={styles.focusItemsContainer}>
        <View style={focused ? styles.topArrowFocus : styles.topArrowHidden} />
        <View style={focused ? styles.containerFocus : styles.container}>
          {isLoading ? (
            <View style={styles.centerContainer}>
              <ActivityIndicator noTestID color={Theme.loading} size={'large'} />
            </View>
          ) : error ? (
            <View style={styles.centerContainer}>
              <TWCText noTestID variant={'regularText'}>
                {'An error occured.'}
              </TWCText>
            </View>
          ) : (
            <>
              <TWCText
                style={styles.tileHeader}
                testID={hourlyHourForecastTileTestIDs.hourText}
                variant={'regularText36'}
              >
                {displayString(hour, error)}
              </TWCText>
              <TWCText
                style={styles.tileSubheader}
                testID={hourlyHourForecastTileTestIDs.dayText}
                variant={'regularText24Transparent'}
              >
                {day}
              </TWCText>
              <TWCIcon code={iconCodeExtend} height={78} testID={hourlyHourForecastTileTestIDs.icon} width={78} />
              <TWCText
                style={[
                  styles.tempText,
                  temperature > 0 && {
                    paddingLeft: Theme.px(SIZES.degreeSymbolWidth),
                  },
                ]}
                testID={hourlyHourForecastTileTestIDs.tempText}
                variant={'regularText66'}
              >
                {isDisplayable(temperature) ? `${temperature}˚` : missingValue}
              </TWCText>
              <View style={styles.precipContainer}>
                <TWCText testID={hourlyHourForecastTileTestIDs.precipLabel} variant={'regularText24Transparent'}>
                  {t(`${tPath}precipLabel`)}
                </TWCText>
                <TWCText
                  style={styles.precipText}
                  testID={hourlyHourForecastTileTestIDs.precipText}
                  variant={'regularText24Transparent'}
                >
                  {isDisplayable(precipChance) ? `${precipChance}%` : missingValue}
                </TWCText>
              </View>
            </>
          )}
        </View>
      </View>
    );
  },
);

/**
 * Tile to use on the hourly forecast dashboard. Each tile is one hour in the hourly forecast swimlane.
 */
export const HourlyHourForecastTile = forwardRef(
  (
    {
      validTimeUtc,
      temperature,
      iconCodeExtend,
      dayOfWeek,
      weatherPhrase,
      precipChance,
      onPress,
      isLoading,
      error,
      hasTVPreferredFocus,
      nativeID,
      testID,
      nextFocusDown,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      onFocus,
    },
    reference,
  ) => {
    const { t } = useTranslation();
    const { timeZone } = useRecoilValue(dayDateTimeState);

    let hour, day;

    if (!isLoading && !error && validTimeUtc) {
      const date = TimeUtils.dateFromEpoch(validTimeUtc);
      const timeFormat = TimeUtils.getTimeFormat();

      hour = TimeUtils.getZonedHourTime(date, timeZone, timeFormat);
      day = validTimeUtc ? TimeUtils.dayOfWeekAbbr(date) : null;
    }

    const precipChanceRounded = precipChance ? roundNearest5(precipChance) : null;

    return (
      <FocusableHighlight
        accessibilityHint={t(`${tPath}tileAccessibilityHint`)}
        accessibilityLabel={t(`${tPath}tileAccessibilityLabel`, {
          day: dayOfWeek,
          hour: `${hour}m`, // so talkback says "9pm" instead of "9p"
          weather: weatherPhrase,
          temperature,
          precip: precipChanceRounded,
        })}
        hasTVPreferredFocus={hasTVPreferredFocus}
        nativeID={nativeID || testID}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        style={styles.highlight}
        testID={testID}
        underlayColor={Theme.transparent}
        onFocus={onFocus}
        onPress={onPress}
      >
        <ForecastTileContent
          day={day}
          error={error}
          hour={hour}
          iconCodeExtend={iconCodeExtend}
          isLoading={isLoading}
          precipChance={precipChanceRounded}
          temperature={temperature}
        />
      </FocusableHighlight>
    );
  },
);

HourlyHourForecastTile.propTypes = {
  nativeID: PropTypes.string,
  onPress: PropTypes.func.isRequired,
  testID: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  dayOfWeek: PropTypes.string,
  validTimeUtc: PropTypes.number,
  iconCodeExtend: PropTypes.number,
  weatherPhrase: PropTypes.string,
  temperature: PropTypes.number,
  precipChance: PropTypes.number,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  hasTVPreferredFocus: PropTypes.bool,
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  onFocus: PropTypes.func,
};

HourlyHourForecastTile.defaultProps = {
  nativeID: null,
  isLoading: false,
  dayOfWeek: '',
  validTimeUtc: null,
  iconCodeExtend: null,
  weatherPhrase: '',
  temperature: null,
  precipChance: null,
  error: null,
  hasTVPreferredFocus: false,
  nextFocusDown: null,
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
  onFocus: () => {},
};

const SIZES = {
  borderWidth: 6,
  componentWidth: 265,
  componentHeight: 338,
  degreeSymbolWidth: 19,
  topArrowWidth: 48,
  topArrowHight: 20,
};

const styles = StyleSheet.create({
  centerContainer: {
    alignItems: 'center',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  container: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.05),
    borderRadius: Theme.tileBorderRadius,
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight),
    justifyContent: 'flex-start',
    width: Theme.px(SIZES.componentWidth),
  },
  containerFocus: {
    alignItems: 'center',
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.05),
    borderColor: Theme.whisperWhite,
    borderRadius: Theme.tileBorderRadius,
    borderWidth: Theme.px(SIZES.borderWidth),
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth * 2),
    justifyContent: 'flex-start',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  focusItemsContainer: {
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  highlight: {
    alignItems: 'center',
    flexDirection: 'column',
    height: Theme.px(SIZES.componentHeight + SIZES.borderWidth * 2 + SIZES.topArrowHight),
    justifyContent: 'center',
    width: Theme.px(SIZES.componentWidth + SIZES.borderWidth * 2),
  },
  precipContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: Theme.px(6),
  },
  precipText: {
    color: Theme.mainText,
    marginLeft: Theme.px(6),
  },
  tempText: {
    lineHeight: Theme.px(77),
    marginTop: Theme.px(18),
  },
  tileHeader: {
    lineHeight: Theme.px(42),
    marginTop: Theme.px(19),
  },
  tileSubheader: {
    marginBottom: Theme.px(15),
  },
  topArrowFocus: {
    borderBottomColor: Theme.whisperWhite,
    borderBottomWidth: Theme.px(SIZES.topArrowHight),
    borderLeftColor: Theme.transparent,
    borderLeftWidth: Theme.px(SIZES.topArrowWidth / 2),
    borderRightColor: Theme.transparent,
    borderRightWidth: Theme.px(SIZES.topArrowWidth / 2),
    height: 0,
    width: 0,
  },
  topArrowHidden: {
    height: Theme.px(SIZES.topArrowHight),
    width: Theme.px(SIZES.topArrowWidth),
  },
});

export const TILE_WIDTH = SIZES.borderWidth * 2 + SIZES.componentWidth;
